import Button from "@/components/button"
import CollapsibleCard from "@/components/collapsible-card"
import PickerWithSheet from "@/components/picker-with-sheet"
import Topbar from "@/components/topbar"
import { Icon } from "@/icons/icon"
import { cn, formatShiftTime } from "@/lib/utils"
import { DeptToggleGroup } from "@/packaging/components/dept-toggle-group"
import { PackagingParamSchemaType } from "@/packaging/lib/schema"
import { HistoryParam } from "@/services/packaging"
import useStore from "@/store"
import { MachineOption } from "@/store/packaging"
import { LabelValue } from "@/types/common"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { format, parse } from "date-fns"

export default function PackagingHistoryPage() {
  const navigate = useNavigate()
  const userStore = useStore.use.user()
  const histories = useStore.use.packaging().histories
  const currFormDeptId = useStore.use.packaging().currDeptId
  const [currDeptId, setCurrDeptId] = useState("")
  const [currMachineName, setCurrMachineName] = useState("")
  const depts = useDepts()
  const machines = useMachines()
  const { sheets, details } = useCurrSheets()

  function useDepts() {
    const [depts, setDepts] = useState<LabelValue[]>([])

    useEffect(() => {
      const newDepts = histories
        .filter((history) => history.sheets.at(0)?.departmentName)
        .map((history) => {
          const name = history.sheets.at(0)?.departmentName

          return {
            id: history.id,
            label: name || "",
            value: history.id,
          }
        })

      setDepts(newDepts)

      if (!currDeptId) {
        const isInHistory = newDepts.find((dept) => dept.id === currFormDeptId)

        if (isInHistory) {
          setCurrDeptId(isInHistory.value)
        } else {
          setCurrDeptId(newDepts.at(0)?.value || "")
        }
      }
    }, [histories])

    return depts
  }

  function useMachines() {
    const [machines, setMachines] = useState<MachineOption[]>([])

    useEffect(() => {
      const dept = histories.find((item) => item.id === currDeptId)

      if (!dept) {
        setMachines([])

        return
      }

      const machines: LabelValue[] = []
      const unique = new Set<string>()
      for (const sheet of dept.sheets) {
        unique.add(sheet.machineName)
      }

      for (const machine of unique) {
        machines.push({
          id: machine,
          label: machine,
          value: machine,
        })
      }

      setMachines(machines)
      setCurrMachineName(machines.at(0)?.value || "")
    }, [currDeptId])

    return machines
  }

  interface GroupedHistory {
    id: string
    name: string
    general: HistoryParam[]
    leakages: HistoryParam[]
  }

  interface DeptDetails {
    date: string
    shift: string
  }

  function formatDate(timestamp: string): string {
    try {
      const date = parse(timestamp, "M/d/yyyy, hh:mm:ss a", new Date())

      return format(date, "dd MMMM''yy")
    } catch {
      return ""
    }
  }

  function useCurrSheets() {
    const [sheets, setSheets] = useState<GroupedHistory[]>([])
    const [details, setDetails] = useState<DeptDetails | null>(null)

    useEffect(() => {
      const dept = histories.find((history) => history.id === currDeptId)
      if (!dept) {
        setSheets([])

        return
      }

      const machineSheets = dept.sheets.filter(
        (sheet) => sheet.machineName === currMachineName
      )

      const history = machineSheets.at(0)

      setDetails({
        date: history?.lastUpdatedAtIST
          ? formatDate(history.lastUpdatedAtIST)
          : "",
        shift: history?.shiftName
          ? `${history?.shiftName}(${formatShiftTime(history.shiftStartTime)} - ${formatShiftTime(history.shiftEndTime)})`
          : "",
      })

      const groups: GroupedHistory[] = []
      for (const sheet of machineSheets) {
        const grouped: GroupedHistory = {
          id: sheet.sheetId,
          name: sheet.productName,
          general: [],
          leakages: [],
        }

        for (const param of sheet.parameters) {
          if (param.type !== PackagingParamSchemaType.LEAKAGE) {
            grouped.general.push(param)
          } else {
            param.list = param.list.map((item) => {
              if (item.name !== "Leakage Type") {
                return item
              }

              try {
                return {
                  ...item,
                  value: JSON.parse(item.value) as any,
                }
              } catch {
                return {
                  ...item,
                  value: [],
                }
              }
            })

            grouped.leakages.push(param)
          }
        }

        groups.push(grouped)
      }

      setSheets(groups)
    }, [currMachineName, currDeptId])

    return {
      sheets,
      details,
    }
  }

  async function handleClickBack() {
    await navigate("/packaging/shift")
  }

  function handleChangeDept(deptId: string) {
    setCurrDeptId(deptId)
  }

  return (
    <div className="h-screen overflow-auto">
      <Topbar
        className="justify-start sticky top-0 left-0 z-10"
        title="Last History Sheet"
        subtitle={userStore.plant.name}
        slotSide="left"
      >
        <Button className="mr-4" variant="ghost" onClick={handleClickBack}>
          <Icon name="ArrowRight" className="h-6 w-auto rotate-180" />
        </Button>
      </Topbar>

      <div className="mt-4 px-4">
        <DeptToggleGroup
          className="mb-6 mx-auto"
          type="single"
          options={depts}
          defaultValue={depts.at(0)?.value}
          value={currDeptId}
          onValueChange={handleChangeDept}
        />

        {!!machines.length && (
          <PickerWithSheet
            className="mb-4"
            options={machines}
            value={currMachineName}
            deptId={currDeptId}
            indicators={{}}
            onChange={setCurrMachineName}
          />
        )}

        {sheets.length ? (
          <div>
            {sheets.map((sheet) => (
              <CollapsibleCard key={sheet.id} title={sheet.name}>
                {sheet.general.map((param, index) => (
                  <div key={`${param.type}${index}`}>
                    <h3
                      className={cn(
                        `
                        flex justify-between items-center py-2.5 px-3 
                        bg-band-fill uppercase text-sm font-semibold
                      `,
                        param.type ===
                        PackagingParamSchemaType.GENERAL_INFORMATION &&
                        "sr-only"
                      )}
                    >
                      {param.type}
                    </h3>

                    <div className="grid grid-cols-2 gap-y-4 gap-x-1 px-4 pb-4 mt-4">
                      {param.list.map((item) => (
                        <div key={item.name} className="flex flex-col gap-1 col-span-1">
                          <h4 className="text-label-text text-sm font-medium">
                            {item.name}
                          </h4>
                          <div className="text-base font-medium text-button-black">
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {sheet.leakages.map((param, index) => (
                  <div key={`${param.type}${index}`}>
                    <h3
                      className={cn(
                        `
                        flex justify-between items-center py-2.5 px-3 
                        bg-band-fill uppercase text-sm font-semibold
                      `
                      )}
                    >
                      {param.type} {index + 1}
                    </h3>

                    <div className="grid grid-cols-2 gap-y-4 gap-x-1 px-4 pb-4 mt-4">
                      {param.list.map((item) =>
                        item.name === "Leakage Type" ? (
                          <LeakageCard
                            key={item.name}
                            className="col-span-2"
                            totalLeaked={`${param.list.at(1)?.value}`}
                            totalPackets={`${param.list.at(0)?.value}`}
                            leakages={item.value as any}
                          />
                        ) : (
                          <div key={item.name} className="flex flex-col gap-1 col-span-1">
                            <h4 className="text-label-text text-sm font-medium">
                              {item.name}
                            </h4>
                            <div className="text-base font-medium text-button-black">
                              {item.value}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </CollapsibleCard>
            ))}

            {!!details && (
              <div className="flex gap-2 items-center text-label-text text-sm font-medium mt-4 pb-4">
                <div>Date: {details.date}</div>
                <div className="text-icon">•</div>
                <div>Shift: {details.shift}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-label-text flex justify-center mt-8">
            No sheets submitted
          </div>
        )}
      </div>
    </div>
  )
}

interface LeakageCardProps {
  className?: string
  totalPackets: string
  totalLeaked: string
  leakages: Leakage[]
}

interface Leakage {
  name: string
  count: number
}

function LeakageCard({
  className,
  totalPackets,
  totalLeaked,
  leakages,
}: LeakageCardProps) {
  const avg = 0

  function showFlag() {
    return false
  }

  return (
    <div
      className={cn(
        "border border-input-border rounded-lg w-full text-sm font-normal overflow-hidden",
        className
      )}
    >
      <div className="bg-backdrop py-2 px-3 flex justify-between items-center">
        <div>
          <span className="font-semibold">{totalPackets}</span>
          &nbsp;Packets Checked
        </div>

        <div className="flex items-center gap-3">
          <div className="flex gap-3 items-center h-6 bg-blue-fill border border-blue border-dashed px-2">
            <span className="text-tiny">Leakage</span>{" "}
            <span className="text-sm">{avg} %</span>
          </div>

          {showFlag() && <Icon name="RedFlag" className="h-4 w-auto" />}
        </div>
      </div>

      <div className="py-4 px-3">
        <div className="flex justify-between items-center gap-8">
          <div className="flex flex-col gap-1">
            <span className="text-label-text">Packets Leaked</span>
            <span className="text-base">{totalLeaked}</span>
          </div>

          <div className="flex flex-col gap-1">
            {leakages.map((leakage) => (
              <div
                key={leakage.name}
                className="text-label-text text-tiny font-medium flex items-center gap-1.5"
              >
                <div className="rounded-full bg-backdrop text-button-black size-5 flex items-center justify-center">
                  {leakage.count}
                </div>
                <div>{leakage.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
